/**
 * EndOfDayReport — financial + operational roll-up for the date picker.
 *
 * Issue #32 AC1: "I see dogs checked in, dogs checked out, invoices
 * settled, invoices outstanding, gratuity total per groomer, payment-
 * method breakdown."
 *
 * Receives data from the Reports page; owns no fetch logic of its own
 * so the parent can route different dates through the same component.
 */
import type {
  EndOfDayCollected,
  EndOfDayReport as EndOfDayReportData,
  EndOfDayTender,
} from '../../lib/api.js';
// Sawyer 05 end-of-day-tenders (C4 G4) — Money taken styles (co-located).
import './EndOfDayReport.css';

function formatMoney(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

// Sawyer 05 end-of-day-tenders (C4 G4) — one label per tender. "Card
// terminal" is the settle dialog's word for the record-only terminal tender.
const TENDER_LABELS: Record<EndOfDayTender, string> = {
  cash: 'Cash',
  check: 'Check',
  external_terminal: 'Card terminal',
  card: 'Card (online or on file)',
  store_credit: 'Store credit',
  other: 'Other',
  unrecorded: 'Tender not recorded',
};
/** Always shown, even at $0.00, so a zero reads as a zero (Counter Test). */
const ALWAYS_SHOWN: ReadonlySet<EndOfDayTender> = new Set(['cash', 'check', 'external_terminal']);

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Sawyer 05 end-of-day-tenders (C4 G4) — "Money taken": every payment
 * recorded on this date, by tender, with the day's checks for the deposit.
 * Store credit is shown apart: spending credit is not new money (the top-up
 * was), so it is never inside the total.
 */
function MoneyTaken({ collected }: { collected: EndOfDayCollected }): JSX.Element {
  const rows = collected.byTender.filter(
    (t) => t.tender !== 'store_credit' && (ALWAYS_SHOWN.has(t.tender) || t.cents !== 0),
  );
  const sources = [
    `Visits ${formatMoney(collected.bySource.visits.cents)}`,
    ...(collected.bySource.householdCart.count > 0
      ? [`Household checkouts ${formatMoney(collected.bySource.householdCart.cents)} (${collected.bySource.householdCart.count})`]
      : []),
    ...(collected.bySource.packageSales.count > 0
      ? [`Package sales ${formatMoney(collected.bySource.packageSales.cents)} (${collected.bySource.packageSales.count})`]
      : []),
  ];
  return (
    <section
      className="ferro-report__section ferro-eod-money"
      aria-labelledby="eod-money-title"
      data-testid="eod-money-taken"
    >
      <div className="ferro-eod-money__head">
        <h3 id="eod-money-title" className="ferro-report__section-title">Money taken</h3>
        <p className="ferro-muted ferro-eod-money__sub" data-testid="eod-money-sub">
          Payments recorded on this date
        </p>
      </div>
      {collected.count === 0 ? (
        <p className="ferro-muted" data-testid="eod-collected-empty">
          No payments recorded for this date.
        </p>
      ) : (
        <>
          <div className="ferro-eod-money__total" data-testid="eod-collected-total">
            <span className="ferro-eod-money__total-value">{formatMoney(collected.totalCents)}</span>
            <span className="ferro-muted">{plural(collected.count, 'payment', 'payments')}</span>
          </div>
          <ul className="ferro-eod-money__tenders" aria-label="Money taken by tender">
            {rows.map((t) => (
              <li key={t.tender} className="ferro-eod-money__tender" data-testid={`eod-tender-${t.tender}`}>
                <div className="ferro-eod-money__row">
                  <span className="ferro-eod-money__label">
                    {TENDER_LABELS[t.tender]}
                    {t.count > 1 ? <span className="ferro-muted"> × {t.count}</span> : null}
                    {t.tender === 'unrecorded' ? (
                      <span className="ferro-muted ferro-eod-money__hint">
                        Recorded without saying how it was paid
                      </span>
                    ) : null}
                  </span>
                  <span className="ferro-eod-money__amount">{formatMoney(t.cents)}</span>
                </div>
                {t.tender === 'check' && collected.checks.length > 0 ? (
                  <ul className="ferro-eod-money__checks" aria-label="The day's checks" data-testid="eod-checks">
                    {collected.checks.map((c) => (
                      <li key={c.paymentId} className="ferro-eod-money__check" data-testid={`eod-check-${c.paymentId}`}>
                        <strong>{c.customerName ?? 'Customer'}</strong>
                        {' · '}
                        {formatMoney(c.cents)}
                        {' · '}
                        {c.checkNumber ? `Check #${c.checkNumber}` : 'No check number'}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
          {collected.storeCreditCents > 0 ? (
            <p className="ferro-muted ferro-eod-money__line" data-testid="eod-store-credit">
              Paid from store credit {formatMoney(collected.storeCreditCents)} (not new money)
            </p>
          ) : null}
          <p className="ferro-eod-money__line" data-testid="eod-sources">
            <span className="ferro-muted">Where it came from: </span>
            {sources.join(' · ')}
          </p>
          <p className="ferro-eod-money__line" data-testid="eod-collected-tips">
            <span className="ferro-muted">Tips collected </span>
            {formatMoney(collected.tipCents)}
          </p>
        </>
      )}
    </section>
  );
}

export interface EndOfDayReportProps {
  data: EndOfDayReportData;
}

export function EndOfDayReport({ data }: EndOfDayReportProps): JSX.Element {
  const totalCollected =
    data.paymentMethods.walletCents + data.paymentMethods.cardCents;
  const walletPct =
    totalCollected === 0
      ? 0
      : Math.round((data.paymentMethods.walletCents / totalCollected) * 100);
  // When nothing was collected, neither method has a share — guard against the
  // `100 - 0 = 100%` fallthrough that made "Card" read 100% on an empty day.
  const cardPct = totalCollected === 0 ? 0 : Math.max(0, 100 - walletPct);

  return (
    <div className="ferro-report" data-testid="end-of-day-report">
      <div className="ferro-report__stat-grid">
        <article className="ferro-report__stat" data-testid="stat-checked-in">
          <span className="ferro-report__stat-label">Dogs checked in</span>
          <span className="ferro-report__stat-value">{data.dogsCheckedIn}</span>
        </article>
        <article className="ferro-report__stat" data-testid="stat-checked-out">
          <span className="ferro-report__stat-label">Dogs checked out</span>
          <span className="ferro-report__stat-value">{data.dogsCheckedOut}</span>
        </article>
        <article
          className="ferro-report__stat"
          data-testid="stat-invoices-settled"
        >
          <span className="ferro-report__stat-label">Invoices settled</span>
          <span className="ferro-report__stat-value">
            {data.invoices.settled.count}
          </span>
          <span className="ferro-report__stat-meta">
            {formatMoney(data.invoices.settled.totalCents)}
            {/* Sawyer 05 end-of-day-tenders (G4) — says what it counts beside Money taken. */}
            {data.collected ? ' · for visits on this date' : null}
          </span>
        </article>
        <article
          className="ferro-report__stat"
          data-testid="stat-invoices-outstanding"
        >
          <span className="ferro-report__stat-label">Outstanding</span>
          <span className="ferro-report__stat-value">
            {data.invoices.outstanding.count}
          </span>
          <span className="ferro-report__stat-meta">
            {formatMoney(data.invoices.outstanding.totalCents)}
          </span>
        </article>
      </div>

      {/* Sawyer 05 end-of-day-tenders (C4 G4) — Money taken replaces the
          wallet/card section; an older server (no `collected`) keeps it. */}
      {data.collected ? <MoneyTaken collected={data.collected} /> : (
      <section
        className="ferro-report__section"
        aria-label="Payment-method breakdown"
      >
        <h3 className="ferro-report__section-title">Payment methods</h3>
        <div className="ferro-report__paymethods">
          <div
            className="ferro-report__paymethod"
            data-testid="paymethod-wallet"
          >
            <span className="ferro-report__paymethod-label">Wallet</span>
            <span className="ferro-report__paymethod-value">
              {formatMoney(data.paymentMethods.walletCents)}
            </span>
            <span className="ferro-report__paymethod-pct">{walletPct}%</span>
          </div>
          <div className="ferro-report__paymethod" data-testid="paymethod-card">
            <span className="ferro-report__paymethod-label">Card</span>
            <span className="ferro-report__paymethod-value">
              {formatMoney(data.paymentMethods.cardCents)}
            </span>
            <span className="ferro-report__paymethod-pct">{cardPct}%</span>
          </div>
          <div className="ferro-report__paymethod" data-testid="paymethod-tip">
            <span className="ferro-report__paymethod-label">Tips collected</span>
            <span className="ferro-report__paymethod-value">
              {formatMoney(data.paymentMethods.tipCents)}
            </span>
          </div>
        </div>
        {totalCollected === 0 ? (
          <p className="ferro-muted">No payments collected for this date.</p>
        ) : (
          <div className="ferro-report__pay-bar" aria-hidden="true">
            <div
              className="ferro-report__pay-bar-segment ferro-report__pay-bar-segment--wallet"
              style={{ width: `${walletPct}%` }}
            />
            <div
              className="ferro-report__pay-bar-segment ferro-report__pay-bar-segment--card"
              style={{ width: `${cardPct}%` }}
            />
          </div>
        )}
      </section>
      )}

      <section
        className="ferro-report__section"
        aria-label="Gratuity per groomer"
      >
        <h3 className="ferro-report__section-title">Price adjustments</h3>
        {/* Ted #3 + #9 (#906 #912) — why a visit did not bill at the catalog
            price, with the reason the desk gave. Absent on an older server. */}
        {!data.adjustments || data.adjustments.count === 0 ? (
          <p className="ferro-muted" data-testid="eod-adjustments-empty">
            No price adjustments today.
          </p>
        ) : (
          <ul className="ferro-report__list" aria-label="Price adjustments" data-testid="eod-adjustments">
            {data.adjustments.lines.map((line) => (
              <li key={line.id} className="ferro-report__row" data-testid={`eod-adjustment-${line.id}`}>
                <div>
                  <strong>{line.customerName ?? 'Customer'}</strong>
                  {line.serviceTypeName ? <span className="ferro-muted"> · {line.serviceTypeName}</span> : null}
                  <div className="ferro-muted">
                    {line.reason}
                    {line.staffUsername ? ` — ${line.staffUsername}` : ''}
                  </div>
                </div>
                <div className="ferro-report__row-meta">
                  <span data-testid={`eod-adjustment-delta-${line.id}`}>
                    {line.deltaCents < 0 ? '−' : '+'}
                    {formatMoney(Math.abs(line.deltaCents))}
                  </span>
                  <span className="ferro-muted">
                    {formatMoney(line.originalTotalCents)} → {formatMoney(line.adjustedTotalCents)}
                  </span>
                </div>
              </li>
            ))}
            <li className="ferro-report__row">
              <div>
                <strong>Net</strong>
                <span className="ferro-muted"> · {data.adjustments.count} adjustment{data.adjustments.count === 1 ? '' : 's'}</span>
              </div>
              <div className="ferro-report__row-meta">
                <strong data-testid="eod-adjustments-net">
                  {data.adjustments.netCents < 0 ? '−' : '+'}
                  {formatMoney(Math.abs(data.adjustments.netCents))}
                </strong>
              </div>
            </li>
          </ul>
        )}

        <h3 className="ferro-report__section-title">Gratuity per groomer</h3>
        {data.gratuityByGroomer.length === 0 ? (
          <p className="ferro-muted">No groomer tips collected for this date.</p>
        ) : (
          <ul className="ferro-report__list" aria-label="Groomer tips">
            {data.gratuityByGroomer.map((g) => (
              <li
                key={g.staffId}
                className="ferro-report__row"
                data-testid={`groomer-tip-${g.staffId}`}
              >
                <div>
                  <strong>{g.name}</strong>
                  <span className="ferro-muted"> · {g.role}</span>
                </div>
                <div className="ferro-report__row-meta">
                  <span>{formatMoney(g.tipCents)}</span>
                  <span className="ferro-muted">
                    {g.bookingCount} booking{g.bookingCount === 1 ? '' : 's'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
